import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { UserCommunication } from '@this+that/models';

import { useShortcutsContext } from './ShortcutsContext';
import { ShortcutContext, ShortcutKey } from './types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import {
  useMarkAsReadUserCommunication,
  useMarkAsUnreadUserCommunication,
  useMoveToInboxUserCommunication,
} from '../UserCommunications';
import { useCustomToast } from '../useCustomToast';

export interface UseEmailListActionsProps {
  filteredCommunications: UserCommunication[];
  context: ShortcutContext;
  selectedItems: Set<string>;
  toggleItemSelection: (messageId: string) => void;
  clearSelection?: () => void;
}

function getSelectedEmail(
  filteredCommunications: UserCommunication[],
  selectedEmailIndex: number,
): UserCommunication | null {
  if (
    selectedEmailIndex >= 0 &&
    selectedEmailIndex < filteredCommunications.length
  ) {
    return filteredCommunications[selectedEmailIndex] ?? null;
  }
  return null;
}

export function useEmailListActions({
  filteredCommunications,
  context,
  selectedItems,
  toggleItemSelection,
  clearSelection,
}: UseEmailListActionsProps) {
  const { registerShortcut, unregisterShortcut, currentContext } =
    useKeyboardShortcuts();
  const { selectedEmailIndex } = useShortcutsContext();
  const { t } = useTranslation();
  const { promiseToast } = useCustomToast();

  const { handleMoveToInboxUserCommunication } =
    useMoveToInboxUserCommunication();
  const { handleMarkAsReadUserCommunication } =
    useMarkAsReadUserCommunication();
  const { handleMarkAsUnreadUserCommunication } =
    useMarkAsUnreadUserCommunication();

  useEffect(() => {
    const ctx = [context];
    unregisterShortcut(ShortcutKey.SELECT, ctx);
    unregisterShortcut(ShortcutKey.MOVE_TO_INBOX, ctx);
    unregisterShortcut(ShortcutKey.MARK_AS_READ, ctx);
    unregisterShortcut(ShortcutKey.MARK_AS_UNREAD, ctx);

    registerShortcut({
      key: ShortcutKey.SELECT,
      contexts: [context],
      handler: () => {
        const selectedEmail = getSelectedEmail(
          filteredCommunications,
          selectedEmailIndex,
        );
        if (selectedEmail) {
          toggleItemSelection(selectedEmail.messageId);
        }
      },
      description: 'Select/deselect current email',
    });

    const moveToInboxHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        clearSelection?.();

        promiseToast(
          handleMoveToInboxUserCommunication(itemsArray),
          t('inbox.userCommunication.actions.movingToInbox'),
          t('inbox.userCommunication.actions.moveToInboxMultipleSuccess', {
            count: itemsArray.length,
          }),
          t('inbox.userCommunication.actions.moveToInboxError'),
        );
      } else {
        const selectedEmail = getSelectedEmail(
          filteredCommunications,
          selectedEmailIndex,
        );
        if (selectedEmail) {
          handleMoveToInboxUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.MOVE_TO_INBOX,
      contexts: [context],
      handler: moveToInboxHandler,
      description: `Move selected email(s) back to inbox`,
    });

    const markAsReadHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        promiseToast(
          handleMarkAsReadUserCommunication(itemsArray, clearSelection),
          t('inbox.userCommunication.actions.markingAsRead'),
          t('inbox.userCommunication.actions.markAsReadMultipleSuccess', {
            count: selectedItems.size,
          }),
          t('inbox.userCommunication.actions.markAsReadError'),
        );
      } else {
        const selectedEmail = getSelectedEmail(
          filteredCommunications,
          selectedEmailIndex,
        );
        if (selectedEmail) {
          handleMarkAsReadUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.MARK_AS_READ,
      contexts: [context],
      handler: markAsReadHandler,
      description: `Mark selected email(s) as read`,
    });

    const markAsUnreadHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        promiseToast(
          handleMarkAsUnreadUserCommunication(itemsArray, clearSelection),
          t('inbox.userCommunication.actions.markingAsUnread'),
          t('inbox.userCommunication.actions.markAsUnreadMultipleSuccess', {
            count: selectedItems.size,
          }),
          t('inbox.userCommunication.actions.markAsUnreadError'),
        );
      } else {
        const selectedEmail = getSelectedEmail(
          filteredCommunications,
          selectedEmailIndex,
        );
        if (selectedEmail) {
          handleMarkAsUnreadUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.MARK_AS_UNREAD,
      contexts: [context],
      handler: markAsUnreadHandler,
      description: `Mark selected email(s) as unread`,
      shiftKey: true,
    });

    return () => {
      unregisterShortcut(ShortcutKey.SELECT, ctx);
      unregisterShortcut(ShortcutKey.MOVE_TO_INBOX, ctx);
      unregisterShortcut(ShortcutKey.MARK_AS_READ, ctx);
      unregisterShortcut(ShortcutKey.MARK_AS_UNREAD, ctx);
    };
  }, [
    registerShortcut,
    unregisterShortcut,
    selectedEmailIndex,
    filteredCommunications,
    selectedItems,
    toggleItemSelection,
    clearSelection,
    handleMoveToInboxUserCommunication,
    handleMarkAsReadUserCommunication,
    handleMarkAsUnreadUserCommunication,
    currentContext,
    context,
    t,
    promiseToast,
  ]);
}
